import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Collapse, Divider, Flex, message, Skeleton } from "antd";
import { ReactComponent as LogoDistroStrm } from "shared/assets/images/logo-distro-strm.svg";
import { useSelector } from "react-redux";
import { ArtistState } from "explorer/reducers/artistas";
import { useHistory } from "react-router-dom";
import { DistributionStatusEnum } from "shared/enums/DistributionStatusEnum";
import { ReactComponent as IconDiamond } from "shared/assets/images/icon-diamante-selo.svg";
import { ReactComponent as StrmDistroTitleIcon } from "shared/assets/images/strm-distro-title-icon.svg";
import imgAnnoDominiLogoDistro from "shared/assets/images/anno-domini-logo-distro.png";
import { ClientSpecialCampaignApprovalEnum } from "shared/enums/ClientSpecialCampaignApprovalEnum";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Modais } from "shared/components";
import { getLoggedUserDistroToken } from "shared/service/StrmService";
import { openNewTab } from "shared/helpers/window-helpers";
import "./CentralDistribuicao.scss";

interface CTADistroStrmProps {
  distroTokens: any[];
}

const CTADistroStrm = (props: CTADistroStrmProps) => {
  const { t } = useTranslation();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { distroTokens } = props;

  const distroToken = distroTokens?.find((x) => x.provider === null)?.token;
  const distroActionAvailable =
    userArtist?.distribution?.available === true ||
    userArtist?.distribution?.status === DistributionStatusEnum.Pending ||
    userArtist?.distribution?.status === DistributionStatusEnum.Signed ||
    distroToken;

  const isSignedDistribution =
    userArtist?.distribution?.status === DistributionStatusEnum.Signed;
  const history = useHistory();

  const onClickActionButton = () => {
    const distroToken = distroTokens?.find((x) => x.provider === null)?.token;

    if (isSignedDistribution || distroToken) {
      if (distroToken) {
        const url = `${process.env.REACT_APP_REVELATOR_SITE}authorize?token=${distroToken}`;
        openNewTab(url);
      } else {
        message.error(t("pages.DistributionCenter.distroTokenNotFound"));
      }
    } else {
      history.push("/distribua");
    }
  };

  return (
    <div
      className="cta mobile"
      style={{ display: "flex", flexDirection: "row" }}
    >
      <div className="photo-cta photo-cta-distro-strm">
        <div className="photo-cta-gradient"></div>
        <LogoDistroStrm className="logo-distro-strm" />
      </div>
      <div className="content-cta">
        <div className="badge-cta" hidden={isSignedDistribution}>
          {t("pages.DistributionCenter.recommendedForYou")}
        </div>
        <p className="title-cta">
          {t("pages.DistributionCenter.strmSmartDistribution")}
        </p>
        <p className="subtitle-cta">
          {t("pages.DistributionCenter.distributeSmarterGrowFaster")}
        </p>
        <p className="description-cta">
          {t("pages.DistributionCenter.distributeStrmDescription")}
        </p>
        <div
          className="container-btn-cta"
          style={{ display: "flex", flexDirection: "row" }}
          hidden={!distroActionAvailable}
        >
          <button className="botao-roxo" onClick={onClickActionButton}>
            {isSignedDistribution
              ? t("pages.DistributionCenter.distributeNow")
              : t("pages.DistributionCenter.startDistributeNow")}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CTADigitalLabelProps {
  specialCampaign: string;
  distroTokens: any[];
}

const CTADigitalLabel = (props: CTADigitalLabelProps) => {
  const { specialCampaign, distroTokens } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const [openModalRequest, setOpenModalRequest] = useState(false);

  const specialCampaignData = userArtist?.specialCampaign?.find(
    (x) => x.name === specialCampaign
  );
  const isSignedDistribution = specialCampaignData?.isParticipating;
  const isPendingApproval =
    specialCampaignData?.approval === ClientSpecialCampaignApprovalEnum.Pending;
  const isApproved =
    specialCampaignData?.approval ===
    ClientSpecialCampaignApprovalEnum.Approved;
  const showBadge = !isPendingApproval && !isApproved;

  const onClickActionButton = () => {
    if (isSignedDistribution) {
      const distroToken = distroTokens?.find(
        (x) => x.provider === specialCampaign
      )?.token;
      if (distroToken) {
        const url = `${process.env.REACT_APP_REVELATOR_SITE}authorize?token=${distroToken}`;
        openNewTab(url);
      } else {
        message.error(t("pages.DistributionCenter.distroTokenNotFound"));
      }
    } else if (isApproved) {
      history.push("/distribua", { specialCampaign: specialCampaign });
    } else if (isPendingApproval) {
      setOpenModalRequest(true);
    }
  };

  const getBadgeText = () => {
    if (isPendingApproval) {
      return (
        <>
          <ClockCircleOutlined />{" "}
          {t("pages.DistributionCenter.waitingForApproval")}
        </>
      );
    }
    return null;
  };

  const getTextActionButton = () => {
    if (isSignedDistribution) {
      return t("pages.DistributionCenter.distributeNow");
    } else if (isPendingApproval) {
      return t("pages.DistributionCenter.learnMore");
    } else if (isApproved) {
      return t("pages.DistributionCenter.distributeNow");
    }

    return t("pages.DistributionCenter.startDistributeNow");
  };

  const getTitleText = () => {
    if (isSignedDistribution) {
      return t("pages.DistributionCenter.youArePartOfTheExclusiveGroup");
    } else if (isPendingApproval) {
      return t("pages.DistributionCenter.weHaveReceivedYourRequest");
    } else if (isApproved) {
      return t("pages.DistributionCenter.congratulationsYouHaveBeenApproved");
    }

    return "";
  };

  const getDescriptionText = () => {
    if (isSignedDistribution) {
      return t("pages.DistributionCenter.emailSent");
    } else if (isPendingApproval) {
      return t("pages.DistributionCenter.ourTeamWillBeInTouchSoonViaEmail");
    } else if (isApproved) {
      return t("pages.DistributionCenter.clickForMoreDetails");
    }

    return "";
  };

  return (
    <div
      className="cta digital-label-cta"
      style={{ display: "flex", flexDirection: "row" }}
    >
      <Modais.ModalCampaignADRequest
        open={openModalRequest}
        onCancel={() => setOpenModalRequest(false)}
      />
      <div className="photo-cta">
        <img
          className="logo-image"
          src={imgAnnoDominiLogoDistro}
          alt="Anno Domini"
        />
      </div>
      <div className="content-cta">
        <div
          className={`badge-cta ${isPendingApproval && "pending"}`}
          hidden={showBadge}
        >
          {getBadgeText()}
        </div>
        <p className="title-cta">{getTitleText()}</p>
        <p className="description-cta">{getDescriptionText()}</p>
        <div
          className="container-btn-cta"
          style={{ display: "flex", flexDirection: "row" }}
        >
          <button className="botao-roxo" onClick={onClickActionButton}>
            {getTextActionButton()}
          </button>
          <button
            className="botao-link"
            onClick={() =>
              window.open("https://annodomini.strmmusic.ai/", "_blank")
            }
            hidden={isPendingApproval}
          >
            {t("pages.DistributionCenter.goToWebsite")}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DistributionCenterSkeletonProps {
  shouldRenderSkeleton: boolean;
  skeletonRef: React.RefObject<HTMLDivElement>;
  skeletonClasses: string[];
  isLoading: boolean;
}

const DistributionCenterSkeleton = (props: DistributionCenterSkeletonProps) => {
  const { shouldRenderSkeleton, skeletonRef, skeletonClasses, isLoading } =
    props;

  if (!shouldRenderSkeleton) {
    return null;
  }

  return (
    <div
      ref={skeletonRef}
      className={skeletonClasses.join(" ")}
      aria-hidden={!isLoading}
    >
      <Flex className="header">
        <Skeleton.Input
          active
          style={{ width: 350, height: 32, marginBottom: 10 }}
        />
        <Skeleton.Input
          active
          style={{ width: 500, height: 20, marginBottom: 10 }}
        />
      </Flex>
      <Divider className="divider" />
      <Flex className="ctas-container">
        <div className="title-ctas">
          <Skeleton.Input
            active
            style={{ width: 250, height: 22, marginBottom: 10 }}
          />
        </div>
        <Skeleton.Node
          active
          style={{ width: "100%", height: 285, marginBottom: 10 }}
        />
      </Flex>
      <Divider className="divider" />
      <Flex className="faq">
        <Flex className="header-faq">
          <Skeleton.Input
            active
            style={{ width: 20, height: 20, marginBottom: 10 }}
          />
          <Skeleton.Input
            active
            style={{ width: 250, height: 35, marginBottom: 10 }}
          />
        </Flex>
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 46, marginBottom: 5 }}
        />
      </Flex>
    </div>
  );
};

const TutorialVideoPTLink = "https://www.youtube.com/watch?v=U6uAyScMGmA";
const TutorialVideoENLink = "https://www.youtube.com/watch?v=YmDut7XRaOU";

const transComponents = {
  br: <br />,
  tutorialPt: (
    <a
      href={TutorialVideoPTLink}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Portuguese tutorial video"
    />
  ),
  tutorialEn: (
    <a
      href={TutorialVideoENLink}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="English tutorial video"
    />
  ),
};

const CentralDistribuicao = (props: any) => {
  const { t } = useTranslation();
  const { userArtist, loading: loadingUserArtist } = useSelector(
    (state: ArtistState) => state.artists
  );
  const [distroTokens, setDistroTokens] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [displaySkeleton, setDisplaySkeleton] = useState<boolean>(true);
  const skeletonRef = useRef<HTMLDivElement | null>(null);
  const specialCampaignsVisibles = useMemo(() => {
    return userArtist?.specialCampaign?.filter(
      (x) =>
        x.approval === ClientSpecialCampaignApprovalEnum.Pending ||
        x.approval === ClientSpecialCampaignApprovalEnum.Approved ||
        x.isParticipating
    );
  }, [userArtist?.specialCampaign]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const getDistroToken = async () => {
      setLoading(true);
      const response: any = await getLoggedUserDistroToken();
      setDistroTokens(response?.response?.tokens);
      setLoading(false);
    };
    getDistroToken();
  }, []);

  const isLoading = loading || loadingUserArtist;
  const shouldRenderSkeleton = isLoading || displaySkeleton;

  useEffect(() => {
    if (isLoading) {
      setDisplaySkeleton(true);
    }
  }, [isLoading]);

  useEffect(() => {
    const node = skeletonRef.current;
    if (!node) {
      return;
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === "opacity" && !isLoading) {
        setDisplaySkeleton(false);
      }
    };

    node.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      node.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isLoading]);

  const faqKey = useMemo(
    () =>
      `pages.DistributionCenter.${
        specialCampaignsVisibles?.length ? "faqSpecialCampaign" : "faqStrm"
      }`,
    [specialCampaignsVisibles]
  );

  const faqList = useMemo(
    () =>
      t(faqKey, {
        returnObjects: true,
      }) as Array<{
        question: string;
        answer: string;
      }>,
    [t, faqKey]
  );

  const skeletonClasses = ["content", "content--skeleton"];
  if (!isLoading) {
    skeletonClasses.push("content--stacked", "is-leaving");
  } else {
    skeletonClasses.push("is-visible");
  }

  const finalContentClasses = ["content", "content--final"];
  if (!isLoading) {
    finalContentClasses.push("is-visible");
  } else {
    finalContentClasses.push("is-hidden");
  }

  return (
    <div className="distribution-center">
      <div className="content-wrapper">
        <DistributionCenterSkeleton
          shouldRenderSkeleton={shouldRenderSkeleton}
          skeletonRef={skeletonRef}
          skeletonClasses={skeletonClasses}
          isLoading={isLoading}
        />
        <div className={finalContentClasses.join(" ")} aria-hidden={isLoading}>
          <Flex className="header">
            <h1 className="heading-h3">
              {t("pages.DistributionCenter.title")}
            </h1>
            <h3 className="body-regular subtitle">
              {t("pages.DistributionCenter.subtitle")}
            </h3>
          </Flex>
          <Divider className="divider" />
          <Flex
            className="ctas-container"
            hidden={
              !specialCampaignsVisibles ||
              specialCampaignsVisibles?.length === 0
            }
          >
            <div className="title-ctas">
              <p>
                <IconDiamond />
                <span>{t("pages.DistributionCenter.digitalLabel")}</span>
              </p>
            </div>
            {specialCampaignsVisibles?.map((x) => (
              <CTADigitalLabel
                key={x.name}
                specialCampaign={x.name}
                distroTokens={distroTokens}
              />
            ))}
          </Flex>
          <Flex className="ctas-container">
            <div className="title-ctas">
              <p>
                <StrmDistroTitleIcon />
                <span>
                  {t("pages.DistributionCenter.strmSmartDistribution")}
                </span>
              </p>
            </div>
            <CTADistroStrm distroTokens={distroTokens} />
          </Flex>
          <Divider className="divider" />
          <Flex className="faq">
            <Flex className="header-faq">
              <h2 className="title">FAQ</h2>
              <h2 className="subtitle">
                {t("pages.DistributionCenter.faqTitle")}
              </h2>
            </Flex>
            <Collapse
              className="faq-collapse"
              bordered={false}
              expandIconPosition="end"
              items={faqList?.map((faq: any, index: number) => ({
                key: index.toString(),
                label: faq.question,
                children: (
                  <Trans
                    i18nKey={`${faqKey}.${index}.answer`}
                    components={transComponents}
                  />
                ),
              }))}
            />
          </Flex>
        </div>
      </div>
    </div>
  );
};

export default CentralDistribuicao;
